import {
  GetAccount200Response,
  GetAccount200ResponseCollateralsInner,
} from "../../../codegen-api";
import { AllCollaterals, IsUSDC } from "../../../constants/addresses";
import { findMatchingSpotCollateral } from "../../../utils/spotCollateral";
import { transferMax } from "../../../utils/amount/max/transferMax";
import { MaxResult, maxUnavailable } from "../../../utils/amount/max/types";
import { fundsMaxInput } from "../WithdrawModal.helpers";

export function computeCollateralInfo(
  accountData: GetAccount200Response | undefined,
  asset: AllCollaterals,
  tokenAddress: string | undefined
): GetAccount200ResponseCollateralsInner | undefined {
  return (
    findMatchingSpotCollateral(accountData?.collaterals, asset, tokenAddress) ??
    // Fallback for chains where the API omits the collaterals array
    // but does populate top-level balance / available_balance (e.g. DogeOS).
    (IsUSDC(asset) && accountData?.balance != null
      ? ({
          collateral_asset: asset as any,
          balance: accountData.balance,
          available_balance: accountData.available_balance,
          collateral_value: accountData.balance,
          collateral_yield_bearing: false,
          pending_withdrawals: "0",
          unrealized_pnl: "0",
        } as GetAccount200ResponseCollateralsInner)
      : undefined)
  );
}

/**
 * The most that may be transferred, or the reason it is not known.
 *
 * `transferMax` IS `perpWithdrawMax`: a transfer moves collateral out of this
 * account just as a withdrawal does, so it must respect the same margin cap.
 * This form used to apply none at all — it could move what a withdrawal of the
 * same size refused — and compared a USD cap against token amounts.
 */
export function transferMaxResult(
  accountData: GetAccount200Response | undefined,
  asset: AllCollaterals,
  collateralInfo: GetAccount200ResponseCollateralsInner | undefined,
  decimals: number
): MaxResult {
  if (!collateralInfo) return maxUnavailable("balance_unknown");
  return transferMax(fundsMaxInput(accountData, collateralInfo, asset, decimals));
}

/** Transferable amount as plain decimal text, or `undefined` when not known. */
export function computeTransferrableBalance(
  accountData: GetAccount200Response | undefined,
  asset: AllCollaterals,
  collateralInfo: GetAccount200ResponseCollateralsInner | undefined,
  decimals: number
): string | undefined {
  const max = transferMaxResult(accountData, asset, collateralInfo, decimals);
  return max.ok ? max.max : undefined;
}
